import React, { useMemo } from "react";
import { useI18n } from "Hooks";
import _ from "lodash";
import classNames from "classnames";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import withDerivedProps from "lib/withDerivedProps";

import AnimateHeight from "UIComponents/AnimateHeight";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import AIJustification from "UIComponents/AIJustification";

import { STANDARD_BASED_RUBRIC } from "Constants/stringConstants";

import { CircleOutlined, TickCircleFilled } from "@toddle-edu/ds-icons";

import { isUserTypeStaff, isUserTypeStudent } from "Utils/UserTypeHelpers";
import {
  getCalculatedScore,
  isEvaluated,
  getTransformedAssessmentTool,
} from "../../modules/Utils";
import {
  getAssessmentToolStyles,
  getGroupedJustifications,
} from "ClassRoom/modules/utils";
import {
  CODE,
  CRITERIA_DESCRIPTION,
  GRADE_SCALE_VALUE,
  RATING_DESCRIPTION,
} from "../../modules/Constants";
import { Tooltip } from "@toddle-edu/ds-web";

import classes from "./StandardRubricView.scss";

const styles = {
  paddingNone: {
    padding: "0px",
  },
  criteriaCell: {
    padding: "0px",
    height: "min-content",
  },
  aiTick: {
    color: "var(--interactive-tertiary)",
  },
};

const EvaluationIcon = props => {
  const {
    showDot,
    isEvaluatedByAI,
    isEvaluatedByTeacher,
    isEvaluatedByStudent,
    isEvaluationEnable,
  } = props;

  const size = isEvaluationEnable ? "xx-small" : "xxx-small";

  if (!isEvaluatedByStudent && !isEvaluatedByTeacher && !isEvaluatedByAI) {
    return isEvaluationEnable && !showDot ? (
      <CircleOutlined
        variant="subtle"
        className={classes.circleIcon}
        size={size}
      />
    ) : null;
  }

  const Container = ({ children }) => (
    <div className={classes.ticksContainer}>{children}</div>
  );

  if (showDot) {
    return (
      <Container>
        {isEvaluatedByTeacher && !isEvaluatedByAI ? (
          <span className={classes.teacherSelectedDot} />
        ) : null}
        {isEvaluatedByStudent ? (
          <span className={classes.studentSelectedDot} />
        ) : null}
        {isEvaluatedByAI ? <span className={classes.aiSelectedDot} /> : null}
      </Container>
    );
  }

  return (
    <Container>
      {isEvaluatedByTeacher && !isEvaluatedByAI ? (
        <span className={classes.teacherSelectedTick}>
          <TickCircleFilled variant="link" size={size} />
        </span>
      ) : null}
      {isEvaluatedByStudent ? (
        <span className={classes.studentSelectedTick}>
          <TickCircleFilled size={size} />
        </span>
      ) : null}
      {isEvaluatedByAI ? (
        <span className={classes.aiSelectedTick}>
          <TickCircleFilled style={styles.aiTick} size={size} />
        </span>
      ) : null}
    </Container>
  );
};

const CriterionCell = props => {
  const {
    label,
    description,
    score,
    maxScore,
    containerStyle,
    showDescription = true,
    standardCode,
    isStandardBasedRubric,
  } = props;

  if (!label && !description && !(score || maxScore)) return null;

  const labelClass = classNames("text-body", classes.label);
  const subtleItalicClass = classNames("text-body", classes.subtleItalic);
  const descriptionClass = classNames("text-body-s", classes.description);
  const maxscoreClass = classNames("text-body", classes.maxScore);

  return (
    <>
      <div className={classes.cellUpper} style={containerStyle}>
        {label ? (
          <span className={labelClass}>
            {_.trim(label)}

            {standardCode ? (
              <span className={subtleItalicClass}>{` (${standardCode})`}</span>
            ) : null}
          </span>
        ) : null}

        {_.trim(description) ? (
          <AnimateHeight height={showDescription ? "auto" : 0} duration={300}>
            <div className={descriptionClass}>{_.trim(description)}</div>
          </AnimateHeight>
        ) : null}
      </div>
      {!isStandardBasedRubric && maxScore ? (
        <span className={maxscoreClass}>{`${maxScore} pts`}</span>
      ) : null}
    </>
  );
};

const DescriptorCell = props => {
  const {
    descriptor,
    criteriaId,
    isTeacherSelected,
    isStudentSelected,
    isEvaluationEnable,
    isAISelected,
    isStaff,
    isStudent,
    isSplitView,
    handleClick,
    showDot,
    showDescription = true,
    isGeneratingAIResponse,
    showGradeScaleValue,
    isStandardBasedRubric,
  } = props;

  const {
    id: descriptorId,
    gradeScaleValue,
    standardGradeScaleValueDescriptor,
    score,
  } = descriptor;

  const { abbreviation, label: gradeScaleValueLabel } = gradeScaleValue || {};

  const label = gradeScaleValueLabel ? gradeScaleValueLabel : descriptor?.label;

  const description = (() => {
    if (isStandardBasedRubric) {
      return (
        descriptor?.description ??
        standardGradeScaleValueDescriptor?.description
      );
    }

    return descriptor?.description ?? "";
  })();

  const getCellClassName = () => {
    const baseClass = isSplitView
      ? classes.splitViewDescriptor
      : classes.descriptorCell;

    return classNames(baseClass, {
      [classes.descriptorCellWithBorderRight]:
        !isSplitView && !isEvaluationEnable,

      // Selection states
      [classes.teacherSelectedCell]: isTeacherSelected,
      [classes.studentSelectedCell]: isStudentSelected,
      [classes.aiSelectedCell]: isAISelected,

      // Interaction states
      [classes.teacherSelectableCell]:
        isEvaluationEnable && isStaff && !isGeneratingAIResponse,
      [classes.studentSelectableCell]: isEvaluationEnable && isStudent,
      [classes.cursorDefault]: !isEvaluationEnable,
      [classes.borderNone]: !isSplitView && !isEvaluationEnable,

      // Evaluation mode
      [classes.evaluationCell]: isEvaluationEnable && !isSplitView,
    });
  };

  const abbrClass = classNames("text-body", classes.textPrimary);
  const labelClass = classNames("text-body", classes.textPrimary, {
    [classes.ellipsisLabel]: isSplitView,
  });

  const descriptionClass = classNames("text-body-s", classes.description);
  const scoreClass = classNames("text-body", classes.score);

  const handleCellClick = () => {
    if (isEvaluationEnable) {
      handleClick(
        descriptorId,
        criteriaId,
        isTeacherSelected,
        isStudentSelected
      );
    }
  };

  if (isGeneratingAIResponse) {
    return <SkeletonLoader height="80px" />;
  }

  return (
    <div
      className={getCellClassName()}
      onClick={handleCellClick}
      tabIndex={isEvaluationEnable ? 0 : null}
      onKeyDown={e =>
        e.key === "Enter" || e.key === " " ? handleCellClick() : null
      }
      role="button"
    >
      <div className={classes.cellUpper}>
        <div className={classes.descriptorLabelContainer}>
          {abbreviation ? (
            <span>
              <span className={abbrClass}>{abbreviation}</span>

              {showGradeScaleValue &&
              !_.isEmpty(_.trim(label)) &&
              !isSplitView ? (
                <Tooltip
                  tooltip={_.trim(label)}
                  visible={isSplitView}
                  placement={"top"}
                >
                  <span
                    className={classNames(labelClass, classes.subtleItalic)}
                  >{` (${_.trim(label)})`}</span>
                </Tooltip>
              ) : null}
            </span>
          ) : (
            <Tooltip
              tooltip={_.trim(label)}
              visible={isSplitView}
              placement={"top"}
            >
              <span className={labelClass}>{_.trim(label)}</span>
            </Tooltip>
          )}

          <EvaluationIcon
            showDot={isSplitView || showDot}
            isEvaluatedByAI={isAISelected}
            isEvaluatedByTeacher={isTeacherSelected}
            isEvaluatedByStudent={isStudentSelected}
            isEvaluationEnable={isEvaluationEnable}
          />
        </div>

        {_.trim(description) ? (
          <AnimateHeight height={showDescription ? "auto" : 0} duration={300}>
            <div className={descriptionClass}>{_.trim(description)}</div>
          </AnimateHeight>
        ) : null}
      </div>
      {!isStandardBasedRubric && score ? (
        <span className={scoreClass}>{score}</span>
      ) : null}
    </div>
  );
};

const StandardRubricView = props => {
  const {
    isStudent,
    isStaff,
    assessmentTool,
    containerStyle,
    showDot = false,
    isSplitView = false,
    isEvaluationEnable,
    isEvaluatedByStudent,
    toolEvaluationResponses = [],
    updateEvaluationCell,
    sourceFrom,
    showStandardRubricDescription,
    ibPYPElementRatings,
    isGeneratingAIResponse,
    isAiEvaluationEnabled,
    onAcceptAIEvaluation,
    criteriaCellStyle,
    type,
  } = props;

  const { t } = useI18n();

  const { metadata, gradeScale } = assessmentTool || {};

  const isStandardBasedRubric = type === STANDARD_BASED_RUBRIC;
  const isNumericGradeScale = gradeScale?.gradingType === "NUMERIC";

  // Group evaluation responses by creator type
  const groupedResponses = _.groupBy(toolEvaluationResponses, "creatorType");
  const staffResponses = _.get(groupedResponses, "STAFF", []);
  const studentResponses = _.get(groupedResponses, "STUDENT", []);
  const aiResponses = _.get(groupedResponses, "AI", []);

  // Helper function to get base classes with evaluation state
  const getBaseClasses = (baseClass, evaluationClass) =>
    classNames(baseClass, { [evaluationClass]: isEvaluationEnable });

  // Handle cell click for evaluation
  const handleCellClick = (
    descriptorId,
    criteriaId,
    isTeacherSelected,
    isStudentSelected
  ) => {
    const visibleAIResponse =
      isAiEvaluationEnabled &&
      isStaff &&
      _.find(
        aiResponses,
        response => response.rowId === criteriaId && !response.isDeleted
      );

    if (isEvaluationEnable) {
      const columnId =
        (isStaff && isTeacherSelected) || (isStudent && isStudentSelected)
          ? null
          : descriptorId;

      const isDeleteAiRatings =
        !_.isEmpty(columnId) && !_.isEmpty(visibleAIResponse);

      updateEvaluationCell?.({
        rowId: criteriaId,
        columnId,
        isSelected: isTeacherSelected,
        isDeleteAiRatings,
      });

      if (isDeleteAiRatings) {
        onAcceptAIEvaluation({
          rowId: criteriaId,
          columnId,
        });
      }
    }
  };

  // Render the header row
  const renderHeader = () => {
    if (isSplitView) return null;

    const showTotalPointsColumn =
      !isSplitView && isStandardBasedRubric && isNumericGradeScale;

    const headerClass = classNames(
      getBaseClasses(classes.headerRow, classes.evaluationHeaderRow),
      {
        [classes.headerRowWithTotalPoints]: showTotalPointsColumn,
      }
    );

    const cellClass = classNames(classes.cell, classes.criteriaCell, {
      [classes.evaluationCell]: isEvaluationEnable,
    });

    return (
      <div className={headerClass}>
        <div className={cellClass} style={criteriaCellStyle}>
          <span className="text-label">{t("common:criteria")}</span>
        </div>
        <div className={cellClass} style={criteriaCellStyle}>
          <span className="text-label">
            {t("common:capitalize", { text: t("common:ratings") })}
          </span>
        </div>
        {showTotalPointsColumn && (
          <div
            className={classNames(cellClass, classes.totalPointsHeaderCell)}
            style={criteriaCellStyle}
          >
            <span className="text-label">{t("common:points")}</span>
          </div>
        )}
      </div>
    );
  };

  const renderCriterion = criterion => {
    const {
      id: criteriaId,
      label,
      description,
      descriptors,
      maxScore,
      standard,
    } = criterion || {};

    const isClassRoomOrGradeBook = _.includes(
      ["CLASSROOM", "GRADEBOOK"],
      sourceFrom
    );

    // if source is not classroom or grade book then always show description
    // else show description based on state
    const showDescription =
      !isClassRoomOrGradeBook || showStandardRubricDescription;

    const selectedDisplayOptions = metadata?.selectedDisplayOptions || [];

    const showStandardCode =
      _.isNil(metadata) || _.includes(selectedDisplayOptions, CODE);

    const showCriteriaDescription =
      _.isNil(metadata) ||
      _.includes(selectedDisplayOptions, CRITERIA_DESCRIPTION);

    const showRatingDescription =
      _.isNil(metadata) ||
      _.includes(selectedDisplayOptions, RATING_DESCRIPTION);

    const showGradeScaleValue =
      _.isNil(metadata) ||
      _.includes(selectedDisplayOptions, GRADE_SCALE_VALUE);

    const _label = standard?.label || label;

    const aiToolEvaluationResponses = _.filter(
      aiResponses,
      ({ rowId }) => rowId === criteriaId
    );

    const evaluationJustifications = _.compact(
      _.map(aiToolEvaluationResponses, "evaluationJustification.message")
    );

    const aiSelectedDescriptor = _.find(descriptors, {
      id: _.first(aiToolEvaluationResponses)?.columnId,
    });

    const aiEvaluationLabel =
      aiSelectedDescriptor?.label ||
      aiSelectedDescriptor?.gradeScaleValue?.abbreviation;

    if (isSplitView) {
      return (
        <div className={classes.splitViewContainer} key={criteriaId}>
          <CriterionCell
            label={_label}
            description={description}
            containerStyle={styles.criteriaCell}
            showDescription={showDescription && showCriteriaDescription}
            standardCode={showStandardCode && standard?.code}
            isStandardBasedRubric={isStandardBasedRubric}
          />

          <div className={classes.splitViewDescriptorContainer}>
            {_.map(descriptors, descriptor => {
              const isTeacherSelected = isEvaluated(
                staffResponses,
                criteriaId,
                descriptor.id
              );

              const isAISelected = isEvaluated(
                aiResponses,
                criteriaId,
                descriptor.id
              );

              const isStudentSelected =
                isEvaluated(studentResponses, criteriaId, descriptor.id) &&
                isEvaluatedByStudent;

              return (
                <DescriptorCell
                  key={descriptor.id}
                  descriptor={descriptor}
                  criteriaId={criteriaId}
                  isTeacherSelected={isTeacherSelected}
                  isAISelected={isAISelected}
                  isStudentSelected={isStudentSelected}
                  isEvaluationEnable={isEvaluationEnable}
                  isStaff={isStaff}
                  isStudent={isStudent}
                  isSplitView={true}
                  handleClick={handleCellClick}
                  showDot={showDot}
                  showDescription={showDescription && showRatingDescription}
                  isGeneratingAIResponse={isGeneratingAIResponse}
                  showGradeScaleValue={showGradeScaleValue}
                  isStandardBasedRubric={isStandardBasedRubric}
                />
              );
            })}
          </div>
          {isStaff &&
          isAiEvaluationEnabled &&
          !_.isEmpty(evaluationJustifications) ? (
            <div className={classes.aiJustificationContainer}>
              <AIJustification
                justifications={evaluationJustifications}
                aiEvaluationLabel={aiEvaluationLabel}
                isAIResponseAccepted={
                  _.first(aiToolEvaluationResponses)?.isDeleted
                }
                isAIGeneratingResponse={isGeneratingAIResponse}
              />
            </div>
          ) : null}
        </div>
      );
    }

    const showTotalPointsColumn =
      !isSplitView && isStandardBasedRubric && isNumericGradeScale;

    const rowClass = classNames(
      getBaseClasses(classes.row, classes.evaluationRow),
      {
        [classes.rowWithTotalPoints]: showTotalPointsColumn,
      }
    );

    const criteriaCellClass = classNames(classes.criteriaCell, classes.cell, {
      [classes.evaluationCell]: isEvaluationEnable,
    });

    const descriptorsContainerClass = getBaseClasses(
      classes.descriptorsContainer,
      classes.evaluationDescriptorsContainer
    );

    return (
      <div key={criteriaId} className={rowClass}>
        <div className={criteriaCellClass} style={criteriaCellStyle}>
          <CriterionCell
            label={_label}
            description={description}
            maxScore={maxScore}
            containerStyle={styles.paddingNone}
            showDescription={showDescription && showCriteriaDescription}
            standardCode={showStandardCode && standard?.code}
            isStandardBasedRubric={isStandardBasedRubric}
          />
        </div>

        <div className={descriptorsContainerClass}>
          {_.map(descriptors, descriptor => {
            const isTeacherSelected = isEvaluated(
              staffResponses,
              criteriaId,
              descriptor.id
            );

            const isAISelected = isEvaluated(
              aiResponses,
              criteriaId,
              descriptor.id
            );

            const isStudentSelected =
              isEvaluated(studentResponses, criteriaId, descriptor.id) &&
              isEvaluatedByStudent;

            return (
              <DescriptorCell
                key={descriptor.id}
                descriptor={descriptor}
                criteriaId={criteriaId}
                isTeacherSelected={isTeacherSelected}
                isStudentSelected={isStudentSelected}
                isEvaluationEnable={isEvaluationEnable}
                isStaff={isStaff}
                isStudent={isStudent}
                isSplitView={false}
                handleClick={handleCellClick}
                showDot={showDot}
                isAISelected={isAISelected}
                isGeneratingAIResponse={isGeneratingAIResponse}
                showDescription={showDescription && showRatingDescription}
                showGradeScaleValue={showGradeScaleValue}
                isStandardBasedRubric={isStandardBasedRubric}
              />
            );
          })}
        </div>

        {showTotalPointsColumn && (
          <div
            className={classNames(
              getBaseClasses(
                classes.totalPointsCell,
                classes.evaluationTotalPointsCell
              ),
              classes.cell
            )}
          >
            <span className="text-body">{maxScore}</span>
          </div>
        )}
      </div>
    );
  };

  const calculatedScore = useMemo(
    () => {
      return getCalculatedScore({
        toolResponses: toolEvaluationResponses,
        assessmentTool,
        creatorType: "STUDENT",
        ibPYPElementRatings,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toolEvaluationResponses, ibPYPElementRatings]
  );

  const associateScoreTool = _.get(assessmentTool, "associateScoreTool");
  const maxScore = _.get(associateScoreTool, "scoreToolData.maxScore");

  const isEvaluatedByTeacher = _.some(ibPYPElementRatings, {
    ibPYPElementType: "SCORE",
  });

  const showEvaluationTotal =
    !_.isEmpty(associateScoreTool) &&
    maxScore &&
    !isStaff &&
    calculatedScore &&
    (isEvaluatedByStudent || isEvaluatedByTeacher);

  const indicators = assessmentTool?.standardRubricCriterias || [];

  const groupedEvaluationJustifications = getGroupedJustifications({
    indicators,
    criterias: _.first(indicators)?.descriptors,
    toolEvaluationResponses,
  });

  const showGroupedAIJustifications =
    isAiEvaluationEnabled &&
    !isSplitView &&
    !_.isEmpty(groupedEvaluationJustifications);

  const containerClass = getBaseClasses(
    classes.container,
    classes.evaluationContainer
  );

  const rowContainerClass = classNames(
    getBaseClasses(classes.rowsContainer, classes.evaluationRowsContainer),
    { [classes.borderNone]: isSplitView }
  );

  return (
    <>
      <div className={containerClass} style={containerStyle}>
        {renderHeader()}

        <div className={rowContainerClass}>
          {_.map(
            assessmentTool?.standardRubricCriterias || [],
            renderCriterion
          )}
        </div>
        {isStaff && showGroupedAIJustifications ? (
          <div className={classes.aiJustificationContainer}>
            <AIJustification
              justifications={groupedEvaluationJustifications}
              isAIGeneratingResponse={isGeneratingAIResponse}
            />
          </div>
        ) : null}
      </div>

      {/* Self evaluation total for student */}
      {showEvaluationTotal ? (
        <div className={classes.studentScore}>
          <span className={classNames(classes.textPrimary, "text-label")}>
            {`${t("classRoom:rubric_total")}: ${calculatedScore}/${maxScore}`}
          </span>
          <span className={classNames(classes.textSecondary, "text-body-s")}>
            {t("classRoom:standard_rubric_self_evaluation_note")}
          </span>
        </div>
      ) : null}
    </>
  );
};

const mapStateToProps = state => {
  const showStandardRubricDescription = _.get(
    state,
    "classRoom.evaluation.showStandardRubricDescription",
    true
  );

  const userType = _.get(state, "login.userInfo.user_type");

  const isStaff = isUserTypeStaff({ userType });
  const isStudent = isUserTypeStudent({ userType });

  return { isStaff, isStudent, showStandardRubricDescription };
};

const getDerivedProps = props => {
  const { uiType, assessmentTool } = props;
  const { standardRubricStyles = {} } = getAssessmentToolStyles({ uiType });

  const updatedAssessmentTool = getTransformedAssessmentTool(assessmentTool);

  return {
    ...standardRubricStyles,
    assessmentTool: updatedAssessmentTool,
  };
};

export default compose(
  connect(mapStateToProps, null),
  withDerivedProps(getDerivedProps)
)(StandardRubricView);
